// @em/utils/analyticsConsent
//
// ANALYTICS CONSENT (ROADMAP #62 P7, owner 2026-10-01: "Europe asks, rest on") — shared by the app and the marketing
// websites (A10.5), so both ask the same way. Moved here from apps/app `src/utils/analytics/consent.ts` (which re-exports
// it, so the app's imports and `check-analytics` are unchanged).
//
// Google Consent Mode v2, decided in two layers:
//   - GOOGLE decides the DEFAULT by the visitor's IP: analytics is DENIED in the EEA, the UK and Switzerland
//     (`CONSENT_REGIONS`), granted elsewhere. Ads storage is denied everywhere, always — the app shows no ads.
//   - The VISITOR decides for this browser: Allow / Decline, applied as a consent UPDATE over the default (so a decline
//     outside Europe is honoured too). Remembered in a COOKIE on the widest domain the browser allows (`.eventsmagic.com`)
//     so the brand's website and its app (app.eventsmagic.com) share ONE answer — localStorage is per origin, and asked
//     on the site, a European visitor was asked again in the app (owner, 2026-10-13: fix it). localStorage keeps a copy
//     (same-origin tabs hear it through `storage`); an answer found ONLY there — given before the cookie — is promoted
//     to the cookie on the next read.
// The one-time banner asks only when the browser's TIME ZONE is in Europe (`asksForConsent`). The browser cannot see
// the IP Google uses, so the two can disagree at the edges: someone in Europe on a non-European clock is never asked
// and simply stays uncounted (the safe direction); someone elsewhere on a European clock is asked about a default that
// was already on.
//
// ⚠ The choice can be changed later (the app: its profile page, `AnalyticsConsentCard`; a website: its footer's
//   "Cookie settings") — withdrawing must be as easy as
//   giving. A decline also deletes the `_ga` cookies already set, or "Decline" would leave them behind.
// ⚠ Pure except for the storage and cookie helpers, so `check-analytics` RUNS the rules.

export type AnalyticsConsent = 'granted' | 'denied';

export const CONSENT_STORAGE_KEY = 'em_analytics_consent';

/**
 * Where Google denies analytics by default: the EEA (EU 27 + Iceland, Liechtenstein, Norway), the UK and Switzerland —
 * plus France's overseas regions, which are in the EU but carry codes of their own.
 */
export const CONSENT_REGIONS: readonly string[] = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'GP', 'GF', 'MQ', 'RE', 'YT', 'MF',
  'IS', 'LI', 'NO',
  'GB', 'CH',
  // Québec (an ISO 3166-2 subdivision, which Consent Mode accepts): Law 25 wants tracking OFF until the person turns it
  // on (owner, 2026-10-14). ⓘ No banner asks there — a clock cannot tell Montréal from Toronto — so a Québec visitor
  // stays uncounted unless they allow it in Cookie settings: the safe direction.
  'CA-QC',
];

// ⓘ Clocks outside `Europe/` that belong to those places: the Atlantic islands (Iceland, the Azores, Madeira, the Canaries,
//   the Faroes), Svalbard, Cyprus (an `Asia/` zone), Ceuta, and France's overseas regions.
const EUROPEAN_ELSEWHERE = new Set([
  'Atlantic/Reykjavik', 'Atlantic/Azores', 'Atlantic/Madeira', 'Atlantic/Canary', 'Atlantic/Faroe', 'Arctic/Longyearbyen',
  'Asia/Nicosia', 'Asia/Famagusta', 'Africa/Ceuta',
  'America/Guadeloupe', 'America/Martinique', 'America/Cayenne', 'America/Marigot', 'Indian/Reunion', 'Indian/Mayotte',
]);

/** Should this browser be ASKED? Only on a European clock; an unknown zone is not asked (Google's default still applies). */
export const asksForConsent = (timeZone: string | null | undefined): boolean => {
  if (!timeZone) {
    return false;
  }

  return timeZone.startsWith('Europe/') || EUROPEAN_ELSEWHERE.has(timeZone);
};

export const browserTimeZone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

/** A stored value that is not one of the two answers counts as no answer — the visitor is asked again. */
export const parseConsent = (value: string | null | undefined): AnalyticsConsent | null =>
  value === 'granted' || value === 'denied' ? value : null;

/** The answer in a `Cookie` header / `document.cookie`, or null. A junk value counts as none. */
export const consentFromCookies = (cookies: string | null | undefined): AnalyticsConsent | null => {
  const pair = (cookies ?? '')
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${CONSENT_STORAGE_KEY}=`));

  return pair ? parseConsent(pair.slice(CONSENT_STORAGE_KEY.length + 1)) : null;
};

/** A year: long enough not to ask again, short enough to be asked again some day. */
const CONSENT_COOKIE_MAX_AGE = 365 * 24 * 60 * 60;

/**
 * Write the answer as a cookie on the WIDEST domain the browser accepts: from the registrable domain down to the host
 * (a browser silently refuses a public suffix — `co.uk` — so each try is read back), host-only last (localhost, an IP).
 * Every older copy is removed first, so two cookies of one name on different domains can never disagree.
 */
const writeConsentCookie = (consent: AnalyticsConsent): void => {
  try {
    const host = window.location.hostname;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const tail = `; Path=/; SameSite=Lax${secure}`;
    const domains = cookieDomainsOf(host).reverse();

    document.cookie = `${CONSENT_STORAGE_KEY}=; Max-Age=0${tail}`;
    domains.forEach((d) => {
      document.cookie = `${CONSENT_STORAGE_KEY}=; Max-Age=0; Domain=.${d}${tail}`;
    });

    for (const d of domains) {
      document.cookie = `${CONSENT_STORAGE_KEY}=${consent}; Max-Age=${CONSENT_COOKIE_MAX_AGE}; Domain=.${d}${tail}`;

      if (consentFromCookies(document.cookie) === consent) {
        return;
      }
    }

    document.cookie = `${CONSENT_STORAGE_KEY}=${consent}; Max-Age=${CONSENT_COOKIE_MAX_AGE}${tail}`;
  } catch {
    // ⓘ Cookies refused: localStorage still holds the answer for this origin.
  }
};

export const readStoredConsent = (): AnalyticsConsent | null => {
  try {
    const shared = consentFromCookies(document.cookie);

    if (shared) {
      return shared;
    }
  } catch {
    // ⓘ No cookie access: localStorage below.
  }

  try {
    const local = parseConsent(window.localStorage.getItem(CONSENT_STORAGE_KEY));

    // An answer given before the shared cookie existed: share it now, so the other site does not ask again.
    if (local) {
      writeConsentCookie(local);
    }

    return local;
  } catch {
    return null;
  }
};

/**
 * What applies on this browser as far as the APP can tell: the stored answer, else the default for its clock. ⓘ Shown by
 * the profile switch; Google's own default goes by IP and may be stricter for a traveller (see the file header).
 */
/**
 * The browser's own privacy signal — Global Privacy Control (`navigator.globalPrivacyControl`), which several US states
 * (California, Colorado, Connecticut…) make a binding opt-out (owner, 2026-10-14: US first, then Canada, UK, EU).
 * ⓘ A browser that sends it is treated as having DECLINED analytics until the visitor themselves allows it: an explicit
 *   Allow (stored) still wins — the signal is a default, not a lock.
 */
export const privacySignalOn = (nav: unknown = typeof navigator === 'undefined' ? undefined : navigator): boolean =>
  !!nav && (nav as { globalPrivacyControl?: unknown }).globalPrivacyControl === true;

/** The answer to APPLY over the region default: the visitor's own, else a declining privacy signal, else none. */
export const appliedConsent = (stored: AnalyticsConsent | null, signal: boolean): AnalyticsConsent | null =>
  stored ?? (signal ? 'denied' : null);

export const effectiveConsent = (stored: AnalyticsConsent | null, timeZone: string | null, signal = false): AnalyticsConsent => {
  if (stored) {
    return stored;
  }

  if (signal) {
    return 'denied';
  }

  return asksForConsent(timeZone) ? 'denied' : 'granted';
};

/** The banner shows when this browser has not answered AND it is one we ask. */
export const needsConsentPrompt = (stored: AnalyticsConsent | null, timeZone: string | null): boolean =>
  !stored && asksForConsent(timeZone);

/**
 * The cookie domains to clear `_ga` on: the host and every parent (GA sets its cookie on the widest domain it can —
 * `.eventsmagic.com` for `app.eventsmagic.com` — and a cookie is only deleted on the domain it was set on).
 */
export const cookieDomainsOf = (hostname: string): string[] => {
  const parts = hostname.split('.').filter(Boolean);

  if (parts.length < 2 || /^\d+$/.test(parts[parts.length - 1]!)) {
    return [hostname];
  }

  const domains: string[] = [];

  for (let i = 0; i <= parts.length - 2; i += 1) {
    domains.push(parts.slice(i).join('.'));
  }

  return domains;
};

/** Google Analytics' own cookies: `_ga`, and `_ga_<stream>` per data stream. */
export const isGaCookie = (name: string): boolean => name === '_ga' || name.startsWith('_ga_');

const clearGaCookies = (): void => {
  try {
    const names = document.cookie
      .split(';')
      .map((c) => c.split('=')[0]!.trim())
      .filter(isGaCookie);

    names.forEach((name) => {
      document.cookie = `${name}=; Max-Age=0; path=/`;
      cookieDomainsOf(window.location.hostname).forEach((domain) => {
        document.cookie = `${name}=; Max-Age=0; path=/; domain=.${domain}`;
      });
    });
  } catch {
    // ⓘ A browser that refuses cookie access has no GA cookies to clear.
  }
};

// ── The choice, shared by the banner, the profile switch and the provider ──────────────────────────────────────────
type Listener = (consent: AnalyticsConsent) => void;

const listeners = new Set<Listener>();
let watchingOtherTabs = false;

/**
 * An answer given in ANOTHER tab of the app reaches this one too — a Decline there must stop this tab sending, not wait
 * for its next reload. (The browser fires `storage` only in the OTHER tabs.)
 */
const watchOtherTabs = (): void => {
  if (watchingOtherTabs || typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return;
  }

  watchingOtherTabs = true;
  window.addEventListener('storage', (event) => {
    const consent = event.key === CONSENT_STORAGE_KEY ? parseConsent(event.newValue) : null;

    if (consent) {
      listeners.forEach((listener) => listener(consent));
    }
  });
};

export const subscribeConsent = (listener: Listener): (() => void) => {
  watchOtherTabs();
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/** Record the visitor's answer on this browser and apply it at once. */
export const setAnalyticsConsent = (consent: AnalyticsConsent): void => {
  writeConsentCookie(consent);

  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, consent);
  } catch {
    // ⓘ Private mode: the answer still applies for this visit (and the cookie, if the browser kept it).
  }

  // ⚠ Applied FIRST, cookies cleared after — cleared first, the tag could write them again before it heard the decline.
  listeners.forEach((listener) => listener(consent));

  if (consent === 'denied') {
    clearGaCookies();
  }
};
